/**
 * Escritas da página "Metas e Caixinhas" pelos repositórios do app. Caixinha: criar (com "quanto já
 * tem guardado" do Disponível ou à parte), editar, arquivar/reativar, excluir, guardar (do
 * Disponível, dinheiro novo ou outra conta, com "completar e reservar"), resgatar, rendimento
 * (receita ligada), excluir movimento. Meta: criar, editar, excluir, concluir/reabrir, depósito,
 * transformar em caixinha. Os fluxos que tocam conta E caixinha vêm de reservaAccountFlows.
 */
import { addDeposit, completeGoal, createGoal, deleteGoal, getGoalDeposits, removeDeposit, reopenGoal, updateGoal } from '@/db/repositories/goalRepository';
import {
  archiveReserva, createReserva, deleteReserva, getReservaById, getReservaMovements, guardar, isReservaReactivationBlocked, resgatar, unarchiveReserva, unarchiveReservaClamped, updateReserva,
} from '@/db/repositories/reservaRepository';
import {
  excluirMovimentoDaCaixinha, guardarComAporte, guardarDinheiroNovo, reativarComAporte, registrarRendimentoComReceita, transformarMetaEmCaixinha,
} from '@/db/repositories/reservaAccountFlows';
import { createTransfer, creditAccountBalanceAdjustment } from '@/db/repositories/transactionRepository';
import { brl } from '../viewer/lib/format';
import type { PlanningInput, SavingsEditor } from '../viewer/lib/editing';

function reservaInput(input: PlanningInput) {
  return {
    name: input.name, targetAmount: input.targetAmount, deadline: input.deadline, icon: input.icon, color: input.color,
    isEmergencyFund: input.isEmergencyFund, creditCardId: input.creditCardId,
  };
}

// O repositório devolve o valor movido, não o movimento: a diferença antes/depois dá o id (pro Desfazer).
const movementIds = async (reservaId: string) => new Set((await getReservaMovements(reservaId)).map(m => m.id));
const newMovementId = async (reservaId: string, before: Set<string>) => (await getReservaMovements(reservaId)).find(m => !before.has(m.id))?.id ?? null;

export function createSavingsEditor(after: () => Promise<void>): SavingsEditor {
  return {
    async createReserva(input) {
      if (!input.accountId) throw new Error('Escolha a conta da caixinha.');
      const amt = Math.round(input.initialAmount * 100) / 100;
      const base = { ...reservaInput(input), accountId: input.accountId, initialAmount: amt > 0 ? amt : undefined };
      // "À parte" (ADR 0021/0167): credita o valor inteiro na conta e reserva o total, sem comer o Disponível.
      if (amt > 0 && input.initialSource === 'apart') await creditAccountBalanceAdjustment(input.accountId, amt);
      const r = await createReserva(amt > 0 && input.initialSource === 'apart' ? { ...base, initialIncoming: amt } : base);
      await after();
      return { id: r.id, saved: r.currentAmount };
    },
    async updateReserva(id, input) { await updateReserva(id, reservaInput(input)); await after(); },
    async archiveReserva(id) { await archiveReserva(id); await after(); },
    async unarchiveReserva(id, mode) {
      if (mode === 'clamp') { await unarchiveReservaClamped(id); await after(); return; }
      if (mode === 'aporte') {
        const r = await getReservaById(id);
        if (!r) throw new Error('Caixinha não encontrada.');
        await reativarComAporte({ id, accountId: r.accountId, frozen: r.currentAmount });
        await after();
        return;
      }
      try { await unarchiveReserva(id); } catch (e) {
        if (isReservaReactivationBlocked(e)) throw Object.assign(new Error(`A conta tem só ${brl(e.available)} livres; a caixinha guardava ${brl(e.frozen)}. Faltam ${brl(e.shortfall)}.`), { blocked: e });
        throw e;
      }
      await after();
    },
    async removeReserva(id) { await deleteReserva(id); await after(); },
    async guardar(reservaId, amount, date, notes, source, topUp) {
      const r = await getReservaById(reservaId);
      if (!r) throw new Error('Caixinha não encontrada.');
      const n = notes ?? undefined;
      const before = await movementIds(reservaId);
      let moved: number;
      if (source.kind === 'new') moved = await guardarDinheiroNovo({ reservaId, accountId: r.accountId, amount, date, notes: n });
      else if (source.kind === 'other') {
        // Como no app: transferência real pra conta da caixinha e o valor entra inteiro guardado.
        await createTransfer({ amount, description: `Guardar em ${r.name}`, date, sourceAccountId: source.accountId, destinationAccountId: r.accountId, isConfirmed: true });
        moved = await guardar(reservaId, amount, date, n, { incoming: amount });
      } else if (topUp) moved = await guardarComAporte({ reservaId, accountId: r.accountId, amount, date, notes: n });
      else moved = await guardar(reservaId, amount, date, n);
      const movementId = moved > 0 ? await newMovementId(reservaId, before) : null;
      await after();
      return { moved, movementId };
    },
    async resgatar(reservaId, amount, date, notes) {
      const before = await movementIds(reservaId);
      const moved = await resgatar(reservaId, amount, date, notes ?? undefined);
      const movementId = moved > 0 ? await newMovementId(reservaId, before) : null;
      await after();
      return { moved, movementId };
    },
    async rendimento(reservaId, amount, date, notes) {
      const r = await getReservaById(reservaId);
      if (!r) throw new Error('Caixinha não encontrada.');
      const before = await movementIds(reservaId);
      const moved = await registrarRendimentoComReceita({ reservaId, accountId: r.accountId, amount, date, notes: notes ?? undefined });
      const movementId = moved > 0 ? await newMovementId(reservaId, before) : null;
      await after();
      return { moved, movementId };
    },
    async removeMovement(id) { await excluirMovimentoDaCaixinha(id); await after(); },
    async createGoal(input) {
      const g = await createGoal({ name: input.name, targetAmount: input.targetAmount ?? 0, deadline: input.deadline, icon: input.icon, color: input.color });
      await after();
      return g.id;
    },
    async updateGoal(id, input) {
      await updateGoal(id, { name: input.name, targetAmount: input.targetAmount ?? 0, deadline: input.deadline, icon: input.icon, color: input.color });
      await after();
    },
    async removeGoal(id) { await deleteGoal(id); await after(); },
    async completeGoal(id) { await completeGoal(id); await after(); },
    async reopenGoal(id) { await reopenGoal(id); await after(); },
    async deposit(goalId, amount, date, notes) {
      const before = new Set((await getGoalDeposits(goalId)).map(d => d.id));
      await addDeposit(goalId, amount, date, notes ?? undefined);
      const id = (await getGoalDeposits(goalId)).find(d => !before.has(d.id))?.id ?? null;
      await after();
      return id;
    },
    async removeDeposit(id) { await removeDeposit(id); await after(); },
    async convertGoal(goalId, input) {
      if (!input.accountId) throw new Error('Escolha a conta da caixinha.');
      const amt = Math.round(input.initialAmount * 100) / 100;
      const r = await transformarMetaEmCaixinha({
        goalId, apart: amt > 0 && input.initialSource === 'apart',
        reserva: { ...reservaInput(input), accountId: input.accountId, initialAmount: amt > 0 ? amt : undefined },
      });
      await after();
      return r.id;
    },
  };
}
